import React from "react";
import { withStyles, createStyles, WithStyles, Theme } from "@material-ui/core/styles";

import Check from "@material-ui/icons/Check";
import Close from "@material-ui/icons/Close";

import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";

import numeral from "numeral";
import { inspectiontype } from "../lib/storage/inspection";
import { genericApi } from "../lib/genericAPI";

const styles = (theme: Theme) =>
  createStyles({
    root: {
      padding: `${theme.spacing.unit * 2}px`,
      maxWidth: 480,
      margin: "0 auto",
      width: "100%",
      boxSizing: "border-box",
    },
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      paddingBottom: `${theme.spacing.unit}px`,
      marginBottom: `${theme.spacing.unit * 2}px`,
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    title: {
      fontSize: "1.1rem",
      fontWeight: 600,
      color: theme.palette.text.primary,
    },
    formCard: {
      padding: `${theme.spacing.unit * 3}px`,
      display: "flex",
      flexDirection: "column",
      gap: `${theme.spacing.unit * 2}px`,
    },
    field: {
      width: "100%",
    },
    ratioRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "baseline",
      padding: `${theme.spacing.unit}px ${theme.spacing.unit * 1.5}px`,
      backgroundColor: theme.palette.grey[100],
      borderRadius: theme.shape.borderRadius,
    },
    ratioLabel: {
      fontSize: "12px",
      fontWeight: 600,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      color: theme.palette.text.secondary,
    },
    ratioValue: {
      fontSize: "1.5rem",
      fontWeight: 700,
      color: theme.palette.error.main,
    },
    actions: {
      display: "flex",
      justifyContent: "flex-end",
      gap: `${theme.spacing.unit}px`,
      marginTop: `${theme.spacing.unit * 2}px`,
    },
    closeButton: {
      minWidth: "44px",
      backgroundColor: "#ffebee",
      borderColor: "#e53935",
      color: "#c62828",
      "&:hover": {
        backgroundColor: "#ffcdd2",
        borderColor: "#c62828",
      },
    },
    confirmButton: {
      minWidth: "44px",
      backgroundColor: "#43a047",
      color: "white",
      "&:hover": {
        backgroundColor: "#2e7d32",
      },
    },
  });

type InspectionInternalsFormProps = {} & WithStyles<typeof styles>;

class InspectionInternalsFormUnstyled extends React.Component<InspectionInternalsFormProps, any> {
  state = {
    classes: undefined,
    handleConfirm: undefined,
    handleClose: undefined,
    handleChange: undefined,
    inspectionid: undefined,
    brix: 0,
    acid: 0,
    ratio: 0,
  };

  constructor(props) {
    super(props);
    this.state.classes = props.classes;
    this.state.handleConfirm = props.handleConfirm;
    this.state.handleClose = props.handleClose;
    this.state.inspectionid = props.inspectionid;
  }

  componentDidMount() {
    this.loadData();
  }

  loadData = async () => {
    const inspectionsGeneric = genericApi<inspectiontype>("inspections");
    inspectionsGeneric.findBy("id", this.state.inspectionid).then((data) => {
      if (data) {
        this.setState(
          {
            brix: data.brix ? Number(data.brix) : 0,
            acid: data.acid ? Number(data.acid) : 0,
          },
          () => {
            this.setState({ ratio: numeral(this.state.brix / this.state.acid).format("0.00") });
          },
        );
      }
    });
  };

  handleSubmit = (e) => {
    e.preventDefault();
    this.state.handleConfirm();
  };

  handleChange = (stateproperty, value) => {
    if (stateproperty == "brix") {
      if (value < 0) {
        value = 0;
      }
      if (value > 19.99) {
        value = 19.99;
      }
    }

    if (stateproperty == "acid") {
      if (value < 0) {
        value = 0;
      }
      if (value > 9.99) {
        value = 9.99;
      }
    }

    this.setState({ [stateproperty]: value }, () => {
      this.setState({ ratio: numeral(this.state.brix / this.state.acid).format("0.00") }, () => {
        const inspectionsGeneric = genericApi<inspectiontype>("inspections");
        inspectionsGeneric.findBy("id", this.state.inspectionid).then((data) => {
          const newData = {
            brix: this.state.brix,
            acid: this.state.acid,
          };
          inspectionsGeneric.update(this.state.inspectionid, { ...data, ...newData }).then(() => {});
        });
      });
    });
  };

  render() {
    const { classes } = this.state;

    return (
      <div className={classes.root}>
        <div className={classes.header}>
          <Typography component="h2" className={classes.title}>
            Internals
          </Typography>
        </div>

        <form
          id={`internals`}
          onSubmit={(e) => {
            this.handleSubmit(e);
          }}
        >
          <Paper elevation={1} className={classes.formCard}>
            <TextField
              className={classes.field}
              label="BRIX"
              value={this.state.brix == 0 ? undefined : this.state.brix}
              onChange={(e) => {
                this.handleChange("brix", e.target.value);
              }}
              type="number"
              InputLabelProps={{ shrink: true }}
              margin="normal"
              variant="outlined"
              inputProps={{ step: "0.01", min: 0, max: 19.99 }}
            />
            <TextField
              className={classes.field}
              label="ACID"
              value={this.state.acid == 0 ? undefined : this.state.acid}
              onChange={(e) => {
                this.handleChange("acid", e.target.value);
              }}
              type="number"
              InputLabelProps={{ shrink: true }}
              margin="normal"
              variant="outlined"
              inputProps={{ step: "0.01", min: 0, max: 9.99 }}
            />
            <div className={classes.ratioRow}>
              <span className={classes.ratioLabel}>Ratio</span>
              <span className={classes.ratioValue}>{this.state.ratio}</span>
            </div>
          </Paper>

          <div className={classes.actions}>
            <Button variant="outlined" className={classes.closeButton} onClick={this.state.handleClose}>
              <Close />
            </Button>
            <Button type="submit" variant="contained" className={classes.confirmButton} onClick={this.handleSubmit}>
              <Check />
            </Button>
          </div>
        </form>
      </div>
    );
  }
}

export default withStyles(styles)(InspectionInternalsFormUnstyled);
